import { useEffect } from "react";

/**
 * Simple per-page SEO without external libs.
 * Sets <title>, description, og:title/description/image, twitter card.
 * For full SSR/static SEO, add react-helmet-async + prerendering later.
 */
export default function useSeo({ title, description, image, url, keywords, robots }) {
  useEffect(() => {
    if (title) document.title = title;
    const setMeta = (key, attr, val) => {
      if (val == null) return;
      let el = document.querySelector(`meta[${key}="${attr}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(key, attr);
        document.head.appendChild(el);
      }
      el.setAttribute("content", val);
    };
    if (description) setMeta("name", "description", description);
    if (keywords) setMeta("name", "keywords", keywords);
    // Default to the strong index policy from index.html; pages may override (e.g. noindex on 404).
    setMeta(
      "name",
      "robots",
      robots || "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
    );
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:image", image);
    setMeta("property", "og:url", url || (typeof window !== "undefined" ? window.location.href : ""));
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", "Rushit Shah");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);

    // canonical
    if (typeof window !== "undefined") {
      let canonical = document.querySelector("link[rel='canonical']");
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", url || window.location.href);
    }
  }, [title, description, image, url, keywords, robots]);
}

/** Inject a JSON-LD <script type="application/ld+json"> block */
export function useJsonLd(data, id = "ld-default") {
  useEffect(() => {
    if (!data) return;
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => { /* leave in head */ };
  }, [data, id]);
}
